// Illustrative roster, shared by the matching stage, month grid and real member calendars.
export const PEOPLE = [
  "Aisha",
  "Marcus",
  "Sofia",
  "Daniel",
  "Priya",
  "James",
  "Mei",
  "Noah",
  "Zara",
  "Ethan",
  "Leila",
  "Oliver",
];
export const ROLES = ["Front desk", "Morning", "Evening", "Control room", "Support", "On call"];
export const SHORT_ROLES = ["FD", "AM", "PM", "CR", "SU", "OC"];
export const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
export const RULES = [
  "Certification",
  "Approved leave",
  "Rest after nights",
  "Contracted hours",
  "Workload balance",
  "Preferences",
];
export const GAP_DAY = 25;

export const GAP_ROLE = 3;
export const dateFor = (day: number) => `2027-02-${String(day + 1).padStart(2, "0")}`;
export const clamp = (n: number) => Math.max(0, Math.min(1, n));

// The twelve tiles that float on the opening. Each one has a counterpart on the
// "Staffing" board (JourneyMatching) that it flies into when the visitor enters.
export type IntroTileKind = "Member" | "Station" | "Constraint";
export type IntroTile = { label: string; kind: IntroTileKind; x: number; y: number };
export const INTRO_TILES: ReadonlyArray<IntroTile> = [
  { label: PEOPLE[0] ?? "", kind: "Member", x: 13, y: 16 },
  { label: ROLES[0] ?? "", kind: "Station", x: 43, y: 9 },
  { label: RULES[0] ?? "", kind: "Constraint", x: 76, y: 15 },
  { label: PEOPLE[1] ?? "", kind: "Member", x: 89, y: 34 },
  { label: RULES[1] ?? "", kind: "Constraint", x: 15, y: 39 },
  { label: ROLES[2] ?? "", kind: "Station", x: 82, y: 62 },
  { label: PEOPLE[2] ?? "", kind: "Member", x: 10, y: 70 },
  { label: RULES[2] ?? "", kind: "Constraint", x: 31, y: 84 },
  { label: ROLES[3] ?? "", kind: "Station", x: 63, y: 87 },
  { label: PEOPLE[3] ?? "", kind: "Member", x: 89, y: 84 },
  { label: RULES[4] ?? "", kind: "Constraint", x: 57, y: 23 },
  { label: ROLES[5] ?? "", kind: "Station", x: 39, y: 69 },
];
export const introTileKey = (kind: IntroTileKind, label: string) => `${kind}:${label}`;
export const INTRO_TILE_KEYS = new Set(
  INTRO_TILES.map((tile) => introTileKey(tile.kind, tile.label)),
);

// The intro has its own negative timeline, so it never consumes "The work".
export const INTRO_LENGTH = 0.08;
// Logical chapter boundaries stay stable; weights control physical scroll distance.
export const JOURNEY_TIMING = [
  { from: -INTRO_LENGTH, to: 0, weight: 1 },
  { from: 0, to: 0.2, weight: 1 },
  { from: 0.2, to: 0.3, weight: 3 },
  { from: 0.3, to: 0.5, weight: 0.75 },
  { from: 0.5, to: 1, weight: 1 },
] as const;
const JOURNEY_DISTANCE = JOURNEY_TIMING.reduce(
  (sum, part) => sum + (part.to - part.from) * part.weight,
  0,
);
export const JOURNEY_HEIGHT_VH = 100 + JOURNEY_DISTANCE * 1750;

export function journeyProgress(scroll: number) {
  let distance = clamp(scroll) * JOURNEY_DISTANCE;
  for (const part of JOURNEY_TIMING) {
    const length = (part.to - part.from) * part.weight;
    if (distance <= length) return part.from + distance / part.weight;
    distance -= length;
  }
  return 1;
}

export function journeyScroll(progress: number) {
  const p = Math.max(-INTRO_LENGTH, Math.min(1, progress));
  let distance = 0;
  for (const part of JOURNEY_TIMING) {
    distance += Math.max(0, Math.min(p, part.to) - part.from) * part.weight;
    if (p <= part.to) break;
  }
  return distance / JOURNEY_DISTANCE;
}

// Keep scenario demonstrations outside the core build-to-publish scroll sequence.
const WORKFLOW_START = journeyScroll(0);
const WORKFLOW_MONTH_END = journeyScroll(0.5);
const WORKFLOW_DELIVER_START = journeyScroll(0.9);
const WORKFLOW_DISTANCE = WORKFLOW_MONTH_END - WORKFLOW_START + 1 - WORKFLOW_DELIVER_START;
export const WORKFLOW_HEIGHT_VH = 500;
export function workflowProgress(scroll: number) {
  const distance = clamp(scroll) * WORKFLOW_DISTANCE;
  const monthDistance = WORKFLOW_MONTH_END - WORKFLOW_START;
  return Math.max(
    0,
    journeyProgress(
      distance < monthDistance
        ? WORKFLOW_START + distance
        : WORKFLOW_DELIVER_START + distance - monthDistance,
    ),
  );
}
export function workflowScroll(progress: number) {
  const distance =
    progress < 0.5
      ? journeyScroll(progress) - WORKFLOW_START
      : WORKFLOW_MONTH_END -
        WORKFLOW_START +
        journeyScroll(Math.max(0.9, progress)) -
        WORKFLOW_DELIVER_START;
  return clamp(distance / WORKFLOW_DISTANCE);
}

// ─── Story autoplay ────────────────────────────────────────────────────
// The story plays itself once its board is pinned. The playhead is a clock
// on this timeline: a beat after the tiles land, then leg by leg — travel
// to the stage's completed state, hold there so it can be read, then let
// the transition run. Scrolling runs the same clock AUTOPLAY_SCROLL_BOOST
// times as fast, holds included.
export const STORY_STOPS = [0, 0.0999, 0.1999, 0.29999, 0.479, 0.99] as const;
export const AUTOPLAY_SCROLL_BOOST = 5;
export const AUTOPLAY_START_HOLD_MS = 500;
export const AUTOPLAY_LEGS: ReadonlyArray<{ to: number; travel: number; hold: number }> = [
  { to: 0.0999, travel: 2500, hold: 1200 }, // Staffing: the board tidies, first checks
  { to: 0.1999, travel: 2500, hold: 1200 }, // Conditions: the rest of the checks, six seats
  { to: 0.29999, travel: 3200, hold: 1200 }, // Next days: the six-day sprint
  { to: 0.39, travel: 2000, hold: 1500 }, // the week, solved
  { to: 0.479, travel: 2500, hold: 1500 }, // the month, planned
  { to: 0.99, travel: 3000, hold: 1500 }, // hand it to the team
  { to: 1, travel: 500, hold: 0 },
];
type Keyframe = { t: number; r: number };
const AUTOPLAY_KEYS: ReadonlyArray<Keyframe> = (() => {
  const keys: Keyframe[] = [{ t: 0, r: 0 }];
  let t = AUTOPLAY_START_HOLD_MS;
  keys.push({ t, r: 0 });
  for (const leg of AUTOPLAY_LEGS) {
    const r = workflowScroll(leg.to);
    t += leg.travel;
    keys.push({ t, r });
    if (leg.hold > 0) {
      t += leg.hold;
      keys.push({ t, r });
    }
  }
  return keys;
})();
export const AUTOPLAY_TOTAL_MS = AUTOPLAY_KEYS[AUTOPLAY_KEYS.length - 1]?.t ?? 0;

/** The story's scroll ratio (0..1) at `time` ms on the autoplay timeline. */
export function playheadRatio(time: number): number {
  const t = Math.max(0, Math.min(AUTOPLAY_TOTAL_MS, time));
  for (let i = 0; i + 1 < AUTOPLAY_KEYS.length; i++) {
    const a = AUTOPLAY_KEYS[i];
    const b = AUTOPLAY_KEYS[i + 1];
    if (!a || !b || t > b.t) continue;
    return b.t === a.t ? b.r : a.r + ((t - a.t) / (b.t - a.t)) * (b.r - a.r);
  }
  return 1;
}

/**
 * The earliest time on the autoplay timeline at which the story shows
 * `ratio` — for a stop, the start of its hold, so a visitor who arrives
 * there by other means still gets the pause.
 */
export function playheadTime(ratio: number): number {
  const r = clamp(ratio);
  for (let i = 0; i + 1 < AUTOPLAY_KEYS.length; i++) {
    const a = AUTOPLAY_KEYS[i];
    const b = AUTOPLAY_KEYS[i + 1];
    if (!a || !b) continue;
    if (r <= a.r) return a.t;
    if (r <= b.r && b.r > a.r) return a.t + ((r - a.r) / (b.r - a.r)) * (b.t - a.t);
  }
  return AUTOPLAY_TOTAL_MS;
}

export function assignmentsFor(day: number, progress = 1): (number | null)[] {
  const people =
    day === 0 ? [1, 3, 4, 5, 6, 7] : ROLES.map((_, role) => (day * 5 + role) % PEOPLE.length);
  if (day > 0) {
    const resting = day === 1 ? 7 : (day * 5) % PEOPLE.length;
    const conflict = people.indexOf(resting);
    const replacement = PEOPLE.map((_, person) => person).findLast(
      (person) => !people.includes(person),
    );
    if (conflict >= 0 && replacement !== undefined) people[conflict] = replacement;
  }
  // A visible fairness improvement: redistribute two duties to an idle eligible colleague.
  if ((day === 8 || day === 10) && progress < 0.65) people[0] = 0;
  if (day === 8 && progress >= 0.65) people[0] = 10;
  if (day === 10 && progress >= 0.65) people[0] = 9;
  // Approved leave arrives; propose an available colleague, keeping the absence protected.
  if (day === 16 && progress >= 0.75) people[4] = 2;
  // An agreed exchange changes only two cells on the same day.
  if (day === 20 && progress >= 0.85) [people[0], people[1]] = [people[1] ?? 0, people[0] ?? 0];
  return people.map((person, role) => (day === GAP_DAY && role === GAP_ROLE ? null : person));
}

export function leaveFor(person: number, day: number, progress: number) {
  return person === 0 && (day === 0 || (day === 16 && progress >= 0.75));
}
